import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import LogLayout from "../components/LogLayout";
import {
  Plus, ListChecks, Truck, ClipboardList, Clock, CheckCircle2, XCircle, Play,
  User, AlertTriangle, Calendar, MapPin, ChevronDown, ChevronUp, Send, Trash2, Filter
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const PRIORITY_STYLES = {
  basse: "bg-white/50/15 text-slate-400 border-slate-500/20",
  normale: "bg-sky-500/15 text-sky-400 border-sky-500/20",
  haute: "bg-amber-500/100/15 text-amber-400 border-amber-500/20",
  urgente: "bg-red-500/100/15 text-red-400 border-red-500/20",
};
const STATUS_STYLES = {
  a_faire: { bg: "bg-white/50/15 border-slate-500/20", text: "text-slate-400", icon: Clock, label: "A faire" },
  en_cours: { bg: "bg-sky-500/15 border-sky-500/20", text: "text-sky-400", icon: Play, label: "En cours" },
  terminee: { bg: "bg-emerald-500/15 border-emerald-500/20", text: "text-emerald-400", icon: CheckCircle2, label: "Terminée" },
  annulee: { bg: "bg-red-500/100/15 border-red-500/20", text: "text-red-400", icon: XCircle, label: "Annulée" },
};

export default function LogTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignées] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expandedTask, setExpandedTask] = useState(null);
  const [form, setForm] = useState({
    task_type: "generale", title: "", description: "", assigned_to: "",
    priority: "normale", due_date: "", pickup_address: "", pickup_contact: "",
    delivery_address: "", delivery_contact: "", pharmacy_name: "",
  });
  const { user: authUser } = useAuth();
  const user = authUser || {};
  const isManager = ["superadmin", "admin"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/log/tasks`, { headers: getHeaders() }),
        isManager ? axios.get(`${API}/log/tasks/stats`, { headers: getHeaders() }) : Promise.resolve({ data: {} }),
      ]);
      setTasks(tRes.data.tasks || []);
      setStats(sRes.data || {});
      if (isManager && assignees.length === 0) {
        const aRes = await axios.get(`${API}/log/tasks/assignees`, { headers: getHeaders() });
        setAssignées(aRes.data.assignees || []);
      }
    } catch { }
    finally { setLoading(false); }
  }, [isManager, assignees.length]);

  useEffect(() => { load(); }, [load]);

  const createTask = async () => {
    if (!form.title || !form.assigned_to) { toast.error("Titre et assignation requis"); return; }
    try {
      await axios.post(`${API}/log/tasks`, form, { headers: getHeaders() });
      toast.success("Tache creee et assignee !");
      setShowForm(false);
      setForm({ task_type: "generale", title: "", description: "", assigned_to: "",
        priority: "normale", due_date: "", pickup_address: "", pickup_contact: "",
        delivery_address: "", delivery_contact: "", pharmacy_name: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const updateStatus = async (taskId, newStatus, note = "") => {
    try {
      await axios.put(`${API}/log/tasks/${taskId}/status`, { status: newStatus, note }, { headers: getHeaders() });
      toast.success(`Statut mis a jour: ${STATUS_STYLES[newStatus]?.label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Supprimer cette tache ?")) return;
    try {
      await axios.delete(`${API}/log/tasks/${taskId}`, { headers: getHeaders() });
      toast.success("Tache supprimee");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = filter === "all" ? tasks : tasks.filter(t => t.status === filter);

  if (loading) return <LogLayout title="Tâches"><div className="flex justify-center py-20"><Clock className="w-6 h-6 text-sky-400 animate-spin" /></div></LogLayout>;

  return (
    <LogLayout title="Gestion des Tâches" subtitle="Assignation de missions aux agents commerciaux et admins">
      <div data-testid="tasks-page" className="space-y-5">
        {/* Stats KPIs */}
        {isManager && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "A faire", val: stats.by_status?.a_faire || 0, color: "#64748b", icon: Clock },
              { label: "En cours", val: stats.by_status?.en_cours || 0, color: "#0ea5e9", icon: Play },
              { label: "Terminées", val: stats.by_status?.terminee || 0, color: "#10b981", icon: CheckCircle2 },
              { label: "Urgentes", val: stats.urgent_pending || 0, color: "#ef4444", icon: AlertTriangle },
            ].map((kpi, i) => (
              <div key={i} className="bg-slate-900/60 border border-white/5 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: kpi.color + "20" }}>
                  <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
                </div>
                <div>
                  <p className="text-xl font-black text-white">{kpi.val}</p>
                  <p className="text-[10px] text-slate-500">{kpi.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions bar */}
        <div className="flex items-center gap-3 flex-wrap">
          {isManager && (
            <Button onClick={() => setShowForm(!showForm)} className="bg-sky-500 hover:bg-sky-400 text-white text-xs" data-testid="new-task-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle tache
            </Button>
          )}
          <div className="flex gap-1 bg-slate-900/60 border border-white/5 rounded-xl p-1">
            {[
              { v: "all", l: "Toutes" },
              { v: "a_faire", l: "A faire" },
              { v: "en_cours", l: "En cours" },
              { v: "terminee", l: "Terminées" },
            ].map(f => (
              <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f.v ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"}`}>
                {f.l}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-500 ml-auto">{filtered.length} tache{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Create form */}
        {showForm && isManager && (
          <div className="bg-sky-500/5 border border-sky-500/20 rounded-xl p-5 space-y-4" data-testid="task-form">
            <h3 className="text-sm font-bold text-sky-400 flex items-center gap-2"><Plus className="w-4 h-4" /> Creer une tache</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Type</label>
                <select value={form.task_type} onChange={e => setForm(p => ({ ...p, task_type: e.target.value }))}
                  className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3 w-full" data-testid="task-type">
                  <option value="generale">Tache generale</option>
                  <option value="course">Course (recuperation/dépôt)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Assigner a</label>
                <select value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))}
                  className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3 w-full" data-testid="task-assignee">
                  <option value="">-- Choisir --</option>
                  {assignees.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name} ({a.role === "coursier" || a.role === "agent commercial" ? "Agent Commercial" : "Admin"})</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Titre</label>
                <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Ex: Recuperer les factures chez Pharmacie du Port" className="bg-slate-900/60 border-white/10 text-white" data-testid="task-title" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Détails de la tache..." rows={2}
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm px-3 py-2 resize-none" data-testid="task-desc" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Priorite</label>
                <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                  className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3 w-full" data-testid="task-priority">
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Échéance</label>
                <Input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                  className="bg-slate-900/60 border-white/10 text-white" data-testid="task-due" />
              </div>
              {form.task_type === "course" && (
                <>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Adresse de recuperation</label>
                    <Input value={form.pickup_address} onChange={e => setForm(p => ({ ...p, pickup_address: e.target.value }))}
                      placeholder="Ex: Pharmacie du Port, Cocody" className="bg-slate-900/60 border-white/10 text-white" data-testid="task-pickup" />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Adresse de dépôt</label>
                    <Input value={form.delivery_address} onChange={e => setForm(p => ({ ...p, delivery_address: e.target.value }))}
                      placeholder="Ex: Siege NSIA Assurances, Plateau" className="bg-slate-900/60 border-white/10 text-white" data-testid="task-delivery" />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={createTask} className="bg-sky-500 hover:bg-sky-400 text-white text-sm" data-testid="submit-task">
                <Send className="w-4 h-4 mr-1.5" /> Creer et assigner
              </Button>
              <Button onClick={() => setShowForm(false)} variant="outline" className="border-white/10 text-slate-400 text-sm">Annuler</Button>
            </div>
          </div>
        )}

        {/* Task list */}
        <div className="space-y-2" data-testid="tasks-list">
          {filtered.length === 0 ? (
            <div className="text-center py-12"><ListChecks className="w-10 h-10 text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-500">Aucune tache.</p></div>
          ) : filtered.map(task => {
            const st = STATUS_STYLES[task.status] || STATUS_STYLES.a_faire;
            const StIcon = st.icon;
            const isExpanded = expandedTask === task.id;
            return (
              <div key={task.id} className={`bg-slate-900/60 border rounded-xl overflow-hidden ${st.bg}`} data-testid={`task-${task.id}`}>
                <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-white/5" onClick={() => setExpandedTask(isExpanded ? null : task.id)}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${st.bg}`}>
                    {task.task_type === "course" ? <Truck className={`w-5 h-5 ${st.text}`} /> : <ClipboardList className={`w-5 h-5 ${st.text}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{task.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border font-bold ${PRIORITY_STYLES[task.priority]}`}>
                        {task.priority.toUpperCase()}
                      </span>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border ${st.bg} ${st.text} font-bold`}>
                        {st.label}
                      </span>
                      <span className="text-[10px] text-slate-500 flex items-center gap-1"><User className="w-3 h-3" />{task.assigned_to_name}</span>
                      {task.due_date && <span className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{task.due_date}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {task.status === "a_faire" && (
                      <Button size="sm" onClick={e => { e.stopPropagation(); updateStatus(task.id, "en_cours"); }}
                        className="bg-sky-500/20 text-sky-400 border border-sky-500/20 text-xs hover:bg-sky-500/30" data-testid={`start-${task.id}`}>
                        <Play className="w-3 h-3 mr-1" /> Demarrer
                      </Button>
                    )}
                    {task.status === "en_cours" && (
                      <Button size="sm" onClick={e => { e.stopPropagation(); updateStatus(task.id, "terminee"); }}
                        className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 text-xs hover:bg-emerald-500/30" data-testid={`complete-${task.id}`}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Terminer
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-white/5 p-4 space-y-3">
                    {task.description && <p className="text-xs text-slate-400">{task.description}</p>}
                    {task.task_type === "course" && (task.pickup_address || task.delivery_address) && (
                      <div className="grid grid-cols-2 gap-3">
                        {task.pickup_address && (
                          <div className="bg-slate-900/40 border border-white/5 rounded-lg p-3">
                            <p className="text-[10px] text-slate-500 uppercase mb-1">Recuperation</p>
                            <p className="text-xs text-white flex items-center gap-1"><MapPin className="w-3 h-3 text-amber-400" />{task.pickup_address}</p>
                          </div>
                        )}
                        {task.delivery_address && (
                          <div className="bg-slate-900/40 border border-white/5 rounded-lg p-3">
                            <p className="text-[10px] text-slate-500 uppercase mb-1">Dépôt</p>
                            <p className="text-xs text-white flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-400" />{task.delivery_address}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500">
                      Créée par <span className="text-white">{task.assigned_by_name}</span> le {new Date(task.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    {task.notes?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] text-slate-500 uppercase">Historique</p>
                        {task.notes.map((n, i) => (
                          <div key={i} className="text-[11px] text-slate-400 bg-slate-900/40 rounded-lg px-3 py-2">
                            <span className="text-white font-semibold">{n.author}</span> — {n.text} <span className="text-slate-600">({new Date(n.created_at).toLocaleString("fr-FR")})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {isManager && (
                      <div className="flex gap-2">
                        {task.status !== "annulee" && task.status !== "terminee" && (
                          <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 text-xs hover:bg-red-500/100/100/10"
                            onClick={() => updateStatus(task.id, "annulee", "Annulée par " + user.full_name)} data-testid={`cancel-${task.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Annuler
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 text-xs hover:bg-red-500/100/100/10"
                          onClick={() => deleteTask(task.id)} data-testid={`delete-${task.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </LogLayout>
  );
}
